import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { ArrowLeft } from "@phosphor-icons/react";

export default function LoginPage() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        const res = await login(email, password);
        setLoading(false);
        if (res.ok) {
            toast.success("Welcome back, Trojan!");
            navigate("/feed");
        } else {
            setError(res.error);
        }
    };

    return (
        <div className="min-h-screen bg-[#FAFAFA] flex" data-testid="login-page">
            {/* Left brand panel */}
            <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-[#990000]">
                <div className="absolute inset-0 opacity-20"
                     style={{
                         backgroundImage: "radial-gradient(circle at 20% 30%, #FFCC00 0%, transparent 40%), radial-gradient(circle at 80% 70%, #FFCC00 0%, transparent 35%)",
                     }} />
                <div className="relative z-10 flex flex-col justify-between p-12 text-white">
                    <Link to="/" className="flex items-center gap-2">
                        <div className="w-9 h-9 rounded-lg bg-[#FFCC00] flex items-center justify-center">
                            <span className="text-[#5C0000] font-heading font-extrabold">T</span>
                        </div>
                        <span className="font-heading font-bold tracking-tight">TrojanTrails</span>
                    </Link>
                    <div>
                        <div className="text-xs uppercase tracking-[0.2em] text-[#FFCC00] font-bold mb-3">USC × INDIA</div>
                        <h2 className="font-heading text-4xl font-bold leading-tight mb-3">
                            Welcome back,<br /> fellow Trojan.
                        </h2>
                        <p className="text-white/70 max-w-sm leading-relaxed">
                            Continue exploring trip ideas, deals and recs shared by your USC family.
                        </p>
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">
                        Fight On · Travel On
                    </div>
                </div>
            </div>

            {/* Form */}
            <div className="flex-1 flex flex-col">
                <div className="p-6">
                    <Link
                        to="/"
                        data-testid="login-back-link"
                        className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-[#990000]"
                    >
                        <ArrowLeft size={16} /> Back to home
                    </Link>
                </div>
                <div className="flex-1 flex items-center justify-center px-6 py-12">
                    <div className="w-full max-w-md">
                        <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#990000] mb-2">Sign in</div>
                        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-[#0A0A0A] mb-2">
                            Sign in to TrojanTrails
                        </h1>
                        <p className="text-stone-500 mb-8">
                            New here?{" "}
                            <Link to="/register" data-testid="login-to-register-link" className="text-[#990000] font-semibold hover:underline">
                                Create an account
                            </Link>
                        </p>

                        <form onSubmit={submit} className="space-y-5">
                            <div>
                                <Label htmlFor="email" className="text-xs uppercase tracking-[0.15em] font-bold text-stone-600">Email</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    data-testid="login-email-input"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@usc.edu"
                                    className="mt-2 h-11 border-stone-300 focus-visible:ring-[#990000] focus-visible:border-[#990000]"
                                    required
                                />
                            </div>
                            <div>
                                <Label htmlFor="password" className="text-xs uppercase tracking-[0.15em] font-bold text-stone-600">Password</Label>
                                <Input
                                    id="password"
                                    type="password"
                                    data-testid="login-password-input"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="••••••••"
                                    className="mt-2 h-11 border-stone-300 focus-visible:ring-[#990000] focus-visible:border-[#990000]"
                                    required
                                />
                            </div>

                            {error && (
                                <div data-testid="login-error" className="text-sm text-[#990000] bg-[#990000]/5 border border-[#990000]/20 rounded-lg px-3 py-2">
                                    {error}
                                </div>
                            )}

                            <Button
                                type="submit"
                                data-testid="login-submit-btn"
                                disabled={loading}
                                className="w-full h-11 bg-[#990000] hover:bg-[#7A0000] text-white font-medium"
                            >
                                {loading ? "Signing in…" : "Sign in"}
                            </Button>

                            <div className="text-center">
                                <Link
                                    to="/forgot-password"
                                    data-testid="login-forgot-link"
                                    className="text-sm text-stone-500 hover:text-[#990000]"
                                >
                                    Forgot password?
                                </Link>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}
